import { Button } from 'omakase';
import { ButtonCoreVariant } from 'omakase/Button/ButtonCore';
import { FC, useEffect, useRef } from 'react';
import { IconChevronSmallRight } from '@tock/icons';
import { Theme, css } from '@emotion/react';
import { core } from '@tock/coretypes';
import { ifClickable } from '@tock/shared/utils/touchUtils';

interface BookingCardSeatingAreaButtonProps {
  className?: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  onClick: (seatingAreaId: number | null) => void;
  seatingArea: core.IAvailabilitySeatingArea;
  buttonVariant?: ButtonCoreVariant;
}

export const BookingCardSeatingAreaButton: FC<BookingCardSeatingAreaButtonProps> = ({
  className = '',
  isDisabled = false,
  isFocused = false,
  onClick,
  seatingArea,
  buttonVariant = 'primary',
  children,
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);

  // For accessibility, when the collapse is expanded and the parent button disappears,
  // we want to focus the first seating area button
  useEffect(() => {
    if (isFocused && btnRef?.current) {
      // Without this timeout, it doesn't focus the button
      setTimeout(() => {
        btnRef.current?.focus();
      }, 100);
    }
  }, [isFocused]);

  return (
    <Button
      className={className}
      disabled={isDisabled}
      data-testid={`seating-area-${seatingArea.id}`}
      onClick={
        !isDisabled
          ? ifClickable(() => {
              onClick(seatingArea.id ?? null);
            })
          : () => undefined
      }
      size="medium"
      fullWidth
      variant={buttonVariant}
      iconEnd={<IconChevronSmallRight />}
      css={(theme: Theme) => css`
        margin-bottom: ${theme.spacing(1)}px;
        > [role='presentation'] {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
      `}
      ref={btnRef}
    >
      <div
        css={() => css`
          display: flex;
          justify-content: space-between;
          width: 100%;
        `}
      >
        {children}
      </div>
    </Button>
  );
};
